import { useState, useEffect } from "react";
import { postsService } from "../services/postsService";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { CreatePostRequest, Post } from "@/core/types/postTypes";

interface UseReelCreatorOptions {
  onSuccess?: (reelId: string, response: any) => void;
  onError?: (error: string) => void;
  editingPost?: Post | null;
}

export const useReelCreator = (
  clientId: string | null,
  options: UseReelCreatorOptions = {},
) => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<MediaItem | null>(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState<MediaItem | null>(null);

  const isEditMode = !!options.editingPost;

  // Initialize state from editing post
  useEffect(() => {
    if (options.editingPost) {
      // Find the video MediaItem from mediaItems
      const videoItem = options.editingPost.mediaItems.find(item => item.isVideo());
      if (videoItem) {
        setSelectedVideo(videoItem);
      }

      // Find thumbnail MediaItem with fallback logic
      let thumbnailItem = null;

      // First try the dedicated thumbnail MediaItem
      if (options.editingPost.thumbnailMediaItem) {
        thumbnailItem = options.editingPost.thumbnailMediaItem;
      }
      // If not available, look for thumbnail using thumbnailMediaId in mediaItems
      else if (options.editingPost.thumbnailMediaId && options.editingPost.mediaItems) {
        const thumbnailId = typeof options.editingPost.thumbnailMediaId === 'string'
          ? parseInt(options.editingPost.thumbnailMediaId)
          : options.editingPost.thumbnailMediaId;

        thumbnailItem = options.editingPost.mediaItems.find(item =>
          item.id === thumbnailId && item.isImage()
        );
      }
      // Last resort: find any image in mediaItems that's not the video
      else if (options.editingPost.mediaItems) {
        thumbnailItem = options.editingPost.mediaItems.find(item => item.isImage());
      }

      if (thumbnailItem) {
        setSelectedThumbnail(thumbnailItem);
      }
    }
  }, [options.editingPost]);

  const validateForm = (): string | null => {
    if (!selectedVideo) {
      return "A video is required for reels";
    }
    if (!selectedThumbnail) {
      return "A thumbnail image is required for reels";
    }
    if (!selectedVideo.isVideo()) {
      return "Selected media must be a video";
    }
    if (!selectedThumbnail.isImage()) {
      return "Selected thumbnail must be an image";
    }
    return null;
  };

  const generateReelId = (): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `reel-${timestamp}-${random}`;
  };

  const saveReel = async (): Promise<boolean> => {
    if (!clientId) {
      const errorMsg = "No client selected";
      setError(errorMsg);
      return false;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return false;
    }

    setIsCreating(true);
    setError(null);

    try {
      if (isEditMode && options.editingPost) {
        // Update existing reel
        const updateData: {
          videoMediaId?: number;
          thumbnailMediaId?: number;
          caption?: string;
          hashtags?: string[];
        } = {};

        // Only include changed fields
        const currentVideo = options.editingPost.mediaItems.find(item => item.isVideo());
        if (selectedVideo && selectedVideo.id !== currentVideo?.id) {
          updateData.videoMediaId = selectedVideo.id;
        }

        const currentThumbnail = options.editingPost.thumbnailMediaItem;
        if (selectedThumbnail && selectedThumbnail.id !== currentThumbnail?.id) {
          updateData.thumbnailMediaId = selectedThumbnail.id;
        }

        // Check if we have any updates to make
        if (Object.keys(updateData).length === 0) {
          setError("No changes detected");
          return false;
        }

        const response = await postsService.updateReel(clientId, options.editingPost.id, updateData);

        options.onSuccess?.(options.editingPost.id, response);
        return true;
      } else {
        // Create new reel
        const reelId = generateReelId();

        const reelData: CreatePostRequest = {
          groupName: null,
          hashtags: null,
          caption: null,
          postId: reelId,
          postType: "reel",
          images: [
            // First item is the video
            {
              ...selectedVideo!.toCreatePostImage(),
              position: 0,
            },
            // Second item is the thumbnail image
            {
              ...selectedThumbnail!.toCreatePostImage(),
              position: 1,
            },
          ],
        };

        const response = await postsService.createOrGetPost(clientId, reelData);

        options.onSuccess?.(reelId, response);
        return true;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} reel`;
      setError(errorMessage);
      options.onError?.(errorMessage);
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setSelectedVideo(null);
    setSelectedThumbnail(null);
    setError(null);
  };

  const clearSelection = () => {
    setSelectedVideo(null);
    setSelectedThumbnail(null);
    setError(null);
  };

  // Clear error when selections change
  useEffect(() => {
    if (error && (selectedVideo || selectedThumbnail)) {
      setError(null);
    }
  }, [selectedVideo, selectedThumbnail, error]);

  return {
    selectedVideo,
    setSelectedVideo,
    selectedThumbnail,
    setSelectedThumbnail,
    saveReel,
    resetForm,
    clearSelection,
    generateReelId,
    isCreating,
    error,
    setError,
    isValid: validateForm() === null,
    hasVideo: !!selectedVideo,
    hasThumbnail: !!selectedThumbnail,
    videoType: selectedVideo?.isShortVideo() ? "short" : selectedVideo?.isLongVideo() ? "long" : null,
  };
};